import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { MailOpen, CalendarDays, MapPin, Check, X, CheckCircle2 } from "lucide-react";
import { format, parseISO } from "date-fns";

function eventTypeLabel(t) {
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
}
function formatDate(d) {
  try { return format(parseISO(d), "EEE, dd MMM yyyy"); } catch { return d; }
}

export default function InvitationsPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/events");
      setEvents(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const today = new Date().toISOString().slice(0, 10);
  const myStatus = (ev) => (ev.assigned || []).find((a) => a.team_member_id === user?.id)?.status || "invited";
  const upcoming = events.filter((ev) => ev.event_date >= today);
  const pending = upcoming.filter((ev) => myStatus(ev) === "invited");
  const responded = upcoming.filter((ev) => myStatus(ev) !== "invited");

  const respond = async (eventId, status) => {
    const current = (events.find((e) => e.id === eventId)?.assigned || []).find((a) => a.team_member_id === user?.id)?.status || "invited";
    if (current === status) return;
    const changing = current !== "invited";
    const currentLabel = current === "available" ? "Available" : "Not Available";
    const ok = await confirm({
      title: status === "available" ? "Confirm you're available?" : "Mark yourself not available?",
      description: changing
        ? `You currently have this marked as "${currentLabel}". ${status === "available" ? "Change it to Available — the studio will count you in for this event." : "Change it to Not Available — the studio may arrange a replacement."}`
        : (status === "available"
            ? "You'll be assigned to this event and the studio will plan around you. Continue?"
            : "The studio will be told you can't take this event. Continue?"),
      confirmText: status === "available" ? "Yes, I'm available" : "Yes, not available",
    });
    if (!ok) return;
    setResponding(`${eventId}-${status}`);
    try {
      await api.patch(`/events/${eventId}/respond`, { status });
      toast.success(status === "available" ? "Marked as available — you're assigned" : "Marked as not available");
      window.dispatchEvent(new Event("invitations-updated"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResponding(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="invitations-page">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Your Assignments</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421]">Invitations</h1>
        <p className="text-[#5C5652] mt-4 max-w-xl">
          Events the studio has invited you to. Confirm your availability so the team can plan.
        </p>
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-6">
            <MailOpen size={20} strokeWidth={1.5} className="text-[#C88270]" />
            <h2 className="font-serif text-3xl tracking-tight text-[#2A2421]">Pending</h2>
            {pending.length > 0 && (
              <span className="text-xs uppercase tracking-[0.25em] text-[#C88270] bg-[#C88270]/10 px-3 py-1 rounded-full">{pending.length}</span>
            )}
          </div>

          {pending.length === 0 ? (
            <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652] mb-16" data-testid="no-pending-invitations">
              You're all caught up — no pending invitations right now.
            </div>
          ) : (
            <div className="space-y-4 mb-16">
              {pending.map((ev) => (
                <div key={ev.id} className="p-6 bg-white border border-[#C88270]/40 flex flex-col md:flex-row md:items-center gap-4" data-testid={`invitation-card-${ev.id}`}>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270] mb-1">{eventTypeLabel(ev.event_type)}</div>
                    <Link to={`/events/${ev.id}`} className="font-serif text-2xl tracking-tight text-[#2A2421] hover:text-[#C88270] transition-colors">{ev.couple_names || ev.title}</Link>
                    <div className="text-sm text-[#5C5652] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1"><CalendarDays size={13} /> {formatDate(ev.event_date)} {ev.ready_by && `· ready by ${ev.ready_by}`}</span>
                      {ev.location && <span className="flex items-center gap-1"><MapPin size={13} /> {ev.location}</span>}
                    </div>
                    {ev.slots?.length > 0 && <div className="text-xs text-[#C88270] mt-1">Slots: {ev.slots.join(", ")}</div>}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => respond(ev.id, "available")} disabled={responding} data-testid={`invitation-available-${ev.id}`}
                      className="inline-flex items-center gap-2 px-5 py-2 bg-[#829384] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#6d7c6f] transition-colors disabled:opacity-50">
                      <Check size={14} /> Available
                    </button>
                    <button onClick={() => respond(ev.id, "unavailable")} disabled={responding} data-testid={`invitation-unavailable-${ev.id}`}
                      className="inline-flex items-center gap-2 px-5 py-2 border border-red-700 text-red-700 text-xs uppercase tracking-[0.2em] hover:bg-red-700 hover:text-white transition-colors disabled:opacity-50">
                      <X size={14} /> Not Available
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {responded.length > 0 && (
            <div data-testid="responded-invitations">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle2 size={20} strokeWidth={1.5} className="text-[#829384]" />
                <h2 className="font-serif text-3xl tracking-tight text-[#2A2421]">Responded</h2>
              </div>
              <div className="bg-white border border-[#EAE7E0]">
                {responded.map((ev) => {
                  const status = myStatus(ev);
                  return (
                    <div key={ev.id} className="flex flex-wrap items-center gap-4 p-5 border-b border-[#EAE7E0] last:border-0" data-testid={`responded-card-${ev.id}`}>
                      <div className="flex-1 min-w-[180px]">
                        <Link to={`/events/${ev.id}`} className="text-sm text-[#2A2421] hover:text-[#C88270]">{ev.couple_names || ev.title}</Link>
                        <div className="text-xs text-[#5C5652]">{formatDate(ev.event_date)} {ev.ready_by && `· ready by ${ev.ready_by}`}</div>
                      </div>
                      <InvitationPill status={status} />
                      <button onClick={() => respond(ev.id, status === "available" ? "unavailable" : "available")} disabled={responding}
                        data-testid={`change-response-${ev.id}`}
                        className="text-[10px] uppercase tracking-[0.25em] text-[#5C5652] hover:text-[#C88270] disabled:opacity-50">
                        Change
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function InvitationPill({ status }) {
  const map = {
    available: { label: "Available", cls: "bg-[#829384]/10 text-[#829384] border-[#829384]/30" },
    unavailable: { label: "Not Available", cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const s = map[status] || map.available;
  return <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.25em] border rounded-full ${s.cls}`}>{s.label}</span>;
}
